// COPIA da TMA#718 di CriGio @ 4638c540: src/lib/burraco/tavoloView.ts (`Ordine`, `ordinaMano`, `ordinaManoPerValore`, `ordinaManoCome`, righe 81-112)
// Cambi: generica sulla carta (le chiavi di seme e rango le da' il gioco: nel CP le carte sono `{ r, s }`), senza matte ne' jolly (il CP non ne ha).
//
import { SEMI_NAPOLETANI, valoreRango } from '@/lib/tavolo/carta';
import type { Asso, Faccia } from '@/lib/tavolo/carta';

// Il tasto «Ordina» alterna due ordini: `seme` (per seme, poi per rango) e `valore` (per rango, poi per seme). Non muta mai la mano: ne ritorna una copia.

/** `seme` = per seme e poi per rango; `valore` = per rango e poi per seme. Il bottone «Ordina» li alterna. */
export type Ordine = 'seme' | 'valore';

/** Le chiavi di ordinamento di una carta: due interi, il piu' piccolo viene prima. */
export interface ChiaviCarta<T> {
  seme: (carta: T) => number;
  rango: (carta: T) => number;
}

/** La mano per seme e poi per rango. Non cambia la mano, la copia. */
export function ordinaPerSeme<T>(mano: readonly T[], k: ChiaviCarta<T>): T[] {
  return [...mano].sort((a, b) => k.seme(a) - k.seme(b) || k.rango(a) - k.rango(b));
}

/** La mano per rango e poi per seme. Non cambia la mano, la copia. */
export function ordinaPerValore<T>(mano: readonly T[], k: ChiaviCarta<T>): T[] {
  return [...mano].sort((a, b) => k.rango(a) - k.rango(b) || k.seme(a) - k.seme(b));
}

export const ordinaManoCome = <T>(mano: readonly T[], ordine: Ordine, k: ChiaviCarta<T>): T[] => (ordine === 'valore' ? ordinaPerValore(mano, k) : ordinaPerSeme(mano, k));

/** L'altro ordine: il tasto «Ordina» alterna. */
export const altroOrdine = (o: Ordine): Ordine => (o === 'seme' ? 'valore' : 'seme');

// ── ordine per FACCIA (scala 40, P6.2): additivo, il resto del file e quello di prima ──────────────────────────────────────────────────────────────────────────────────────

/** Le chiavi d'ordine di una faccia, per ogni mazzo del modulo: francese (semi c d h s, il rango con l'asso dove dice `asso`), jolly (in coda, in tutti e due gli ordini) e napoletano (semi nell'ordine di QScopa, il grado). */
export function chiaviFaccia(asso: Asso): ChiaviCarta<Faccia> {
  const SEMI_FR = { c: 0, d: 1, h: 2, s: 3 } as const;
  return {
    seme: (f) => (f.mazzo === 'francese' ? SEMI_FR[f.seme] : f.mazzo === 'jolly' ? 4 : SEMI_NAPOLETANI.indexOf(f.seme)),
    rango: (f) => (f.mazzo === 'francese' ? valoreRango(f.rango, asso) : f.mazzo === 'jolly' ? 99 : f.grado),
  };
}

const confrontaChiavi = (a: string | number, b: string | number): number => (typeof a === 'number' && typeof b === 'number' ? a - b : String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0);

/**
 * Una mano di carte con faccia (`{ chiave, faccia }`) nell'ordine che si vede: per seme (poi per rango) o per valore (poi per seme), con l'asso basso o alto, i jolly in coda; a parita la chiave, cosi due
 * carte uguali (le due copie) hanno un ordine fisso. Non cambia la mano, la copia. Non sa com'e fatto un mazzo: legge solo la faccia.
 */
export function ordinaPerFaccia<T extends { readonly chiave: string | number; readonly faccia: Faccia }>(mano: readonly T[], ordine: Ordine, asso: Asso): T[] {
  const k = chiaviFaccia(asso);
  const chiavi = (c: T): [number, number] => (ordine === 'valore' ? [k.rango(c.faccia), k.seme(c.faccia)] : [k.seme(c.faccia), k.rango(c.faccia)]);
  return [...mano].sort((a, b) => {
    const [a0, a1] = chiavi(a);
    const [b0, b1] = chiavi(b);
    return a0 - b0 || a1 - b1 || confrontaChiavi(a.chiave, b.chiave);
  });
}
